import { inject, Injectable } from '@angular/core';
import { ApiService } from '../../../shared/services/core/api.service';
import { Observable } from 'rxjs';
import { MarketplaceItemV2 } from './models/marketplace-item-v2.model';
import { MarketplaceBasketItem } from '../models/basket/marketplace-basket-item.model';
import { MemberDetails } from '../../../account/member/models/member-details.model';
import {
  UnifiedCheckoutAddressRequest,
  UnifiedCheckoutCompleteRequest,
  UnifiedCheckoutResponse,
  UnifiedCheckoutSummary,
} from '../../add-to-cart/models/unified-checkout.model';

@Injectable({
  providedIn: 'root',
})
export class MarketplaceV2Service {
  private readonly api = inject(ApiService);

  /** Fetches the full list of marketplace items from the v2 endpoint. */
  fetchMarketplaceItems(): Observable<MarketplaceItemV2[]> {
    return this.api.get<MarketplaceItemV2>('marketplace/v2/items');
  }

  /** Fetches a single marketplace item by ID from the v2 endpoint. */
  fetchMarketplaceItemById(id: number): Observable<MarketplaceItemV2> {
    return this.api.getById<MarketplaceItemV2>('marketplace/items', id.toString());
  }

  /**
   * Submits a basket of items for purchase and returns the full provider response.
   * Used by the v2 purchase flow as an Observable alternative to `purchaseBasketWithProviderResponse`.
   */
  purchaseBasketItems(basket: MarketplaceBasketItem[], memberId?: string, useCardToken: boolean = false): Observable<any> {
    return this.api.post<MarketplaceBasketItem[]>(
      'marketplace/Purchase' + (memberId ? '?memberId=' + memberId : '') + '?useCardToken=' + useCardToken,
      basket
    );
  }

  /**
   * This endpoint is used to update the shipping option of the basket
   */
  updateAdress(orderId: string, memberDetails: MemberDetails) {
    return this.api.post('marketplace/checkout/address' + (orderId ? '?orderId=' + orderId : ''), memberDetails);
  }

  /**
   * This endpoint is used to update the shipping option of the basket
   */
  updateShippingAdress(orderId: string, memberDetails: MemberDetails) {
    return this.api.post('marketplace/checkout/shipping' + (orderId ? '?orderId=' + orderId : ''), memberDetails);
  }

  checkoutStart(body: any) {
    return this.api.post('marketplace/checkout/start', body);
  }

  /**
   * Creates a checkout order for the given basket and returns the order
   * preview, including the server-calculated tax, shipping and totals.
   */
  checkout(body: any): Observable<UnifiedCheckoutResponse> {
    return this.api.post('checkout', body);
  }

  /**
   * Retrieves an existing checkout order by its id, so a page can be reloaded
   * or returned to without creating a duplicate order.
   */
  getCheckout(orderId: string): Observable<UnifiedCheckoutResponse> {
    return this.api.getAnySingle<UnifiedCheckoutResponse>('checkout?orderId=' + orderId);
  }

  /**
   * Sets the billing address on the basket and returns a recalculated payment
   * summary, since changing the address can change the tax due. Only the
   * summary comes back, not the whole order.
   *
   * Note this is the Checkout V4 endpoint, distinct from the older
   * `marketplace/checkout/address` used by updateAdress above. Its body is
   * camelCase, unlike the PascalCase the checkout response returns.
   */
  updateCheckoutAddress(orderId: string, address: UnifiedCheckoutAddressRequest): Observable<UnifiedCheckoutSummary> {
    return this.api.post('checkout/address?orderId=' + orderId, address);
  }

  /**
   * Sets the shipping address on the basket and returns a recalculated summary.
   *
   * The Checkout V4 counterpart to updateCheckoutAddress above, which sets the
   * billing address - same body, different endpoint. An AddressType of
   * DefaultAddress is normalised to ShippingAddress1 by the server.
   *
   * POST api/checkout/shipping?orderId=
   * 404 when the order id is not found.
   */
  updateCheckoutShippingAddress(orderId: string, address: UnifiedCheckoutAddressRequest): Observable<UnifiedCheckoutSummary> {
    return this.api.post('checkout/shipping?orderId=' + orderId, address);
  }

  /**
   * Selects a shipping option on the basket and returns a recalculated summary,
   * with the new shipping cost applied.
   *
   * Sends no body: both the order and the chosen option are query parameters.
   *
   * POST api/checkout/shippingOption?orderId=&shippingOptionId=
   * 400 for an invalid shippingOptionId, 404 when the order id is not found.
   */
  updateCheckoutShippingOption(orderId: string, shippingOptionId: number): Observable<UnifiedCheckoutSummary> {
    return this.api.post(`checkout/shippingOption?orderId=${orderId}&shippingOptionId=${shippingOptionId}`, null);
  }

  /**
   * Validates a gift code and applies it to the basket, returning a
   * recalculated summary with any discount applied.
   *
   * Sends no body: both the order and the code are query parameters. A code
   * already on the basket is a no-op success rather than an error, so the
   * caller does not have to track which codes it has applied.
   *
   * POST api/checkout/giftcode?orderId=&giftCode=
   * 400 when the code is invalid or inactive, 404 when the order id is not found.
   */
  applyCheckoutGiftCode(orderId: string, giftCode: string): Observable<UnifiedCheckoutSummary> {
    // Encoded because a gift code is member-supplied and can carry characters
    // that are not legal unescaped in a query string.
    return this.api.post(`checkout/giftcode?orderId=${orderId}&giftCode=${encodeURIComponent(giftCode)}`, null);
  }

  /**
   * Completes the checkout, submitting the basket to the payment provider and
   * marking it Completed or Failed.
   *
   * If the response's `ProviderActionInformation.NextAction` is anything other
   * than `None`, the caller must action it (3DS, for example) before the
   * purchase is finished.
   *
   * This is the Checkout V4 endpoint, distinct from the older
   * `marketplace/checkout/complete` used by completePurchaseBasketOrder below.
   */
  completeCheckout(orderId: string | null, body: UnifiedCheckoutCompleteRequest) {
    return this.api.post('checkout/complete?orderId=' + orderId, body);
  }

  /**
   * This endpoint is used to purchase marketplace items, it works with merchant and ecash.
   * This is to replace the api/purchase endpoint.
   */
  completePurchaseBasketOrder(
    orderId: string,
    failureUrl: string,
    successUrl: string,
    useCardToken: boolean = true,
    saveAsCardToken: boolean = false,
    postBackUrl = ''
  ) {
    const body = {
      OrderId: orderId,
      FailureUrl: failureUrl,
      SuccessUrl: successUrl,
      PostBackUrl: postBackUrl,
      UseCardToken: useCardToken,
      SaveCardAsToken: saveAsCardToken,
    };

    return this.api.post('marketplace/checkout/complete', body);
  }
}
